import React, { useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import I18n from 'utils/I18n';
import { Markup } from 'interweave';
import Spinner from '@strava/ui/Spinner';
import { minBy, snakeCase } from 'lodash-es';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { devLog } from 'utils/devUtils';
import styles from './feed.scss';

import Activity from './components/Activity';
import SuggestedActivity from './components/SuggestedActivity';
import Post from './components/Post';
import FancyPromo from './components/FancyPromo';
import PerformanceGoalCreated from './components/PerformanceGoalCreated';
import ChallengeJoin from './components/ChallengeJoin';
import SimplePromo from './components/SimplePromo';
import GroupActivity from './components/GroupActivity';
import ClubJoin from './components/ClubJoin';

import { reducer } from './components/shared/feed.reducer';

import { feedTypeProps, pageProps } from './props/feed-prop-types';
import useFetchFeedEntries from './hooks/useFetchFeedEntries';
import useFeedScrollRestoration from './hooks/useFeedScrollRestoration';
import FeedContext from './components/shared/FeedContext';
import { UPDATE_IS_RESTORING_SCROLL_POSITION } from './components/shared/feed.actions';

import { FEED_ENTRY_TYPES, FEED_PAGES } from './constants';
import { logError, logMessage } from '../../utils/sentry';
import { trackV2 } from '../../utils/analytics';
import { getFeedAnalyticsFinishLoadData } from './utils';

// Cursor pagination is key for the feed so this function has been deliberately
// exported so that it can be unit tested separately.
export const computeMinCursorData = (entries) => {
  return {
    minBeforeEntry: minBy(entries, (e) => {
      return e.cursorData.updated_at;
    }),
    minRankEntry: minBy(entries, (e) => {
      return e.cursorData.rank;
    })
  };
};

const Feed = ({
  currentAthleteId,
  page,
  feedType,
  athleteProfileId,
  clubId,
  useMockData,
  preFetchedEntries,
  locale,
  isAutoplayEnabled,
  canCreateClubPost,
  isDeletable
}) => {
  const {
    isLoading,
    error,
    entries,
    pagination,
    loadEntries
  } = useFetchFeedEntries({
    currentAthleteId,
    clubId,
    athleteProfileId,
    feedType,
    page,
    useMockData,
    preFetchedEntries
  });

  const [entityState, dispatch] = useReducer(reducer, {
    numEntries: 0,
    scrollTop: 0,
    page,
    feedType,
    isRestoringScrollPosition: false,
    isActivityVideoMuted: true
  });

  const {
    saveScrollPosition,
    getScrollPosition,
    clearScrollPosition
  } = useFeedScrollRestoration({ ...entityState });

  const providerState = {
    entityState,
    dispatch
  };

  useEffect(() => {
    // Track the number of entries on the initial load.
    if (Array.isArray(preFetchedEntries) && preFetchedEntries.length <= 20) {
      const {
        inventorySize,
        trackedPageCount,
        inventoryPageSizes,
        inventoryEntryTypes
      } = getFeedAnalyticsFinishLoadData(preFetchedEntries);

      trackV2({
        category: 'performance',
        action: 'finish_load',
        element: 'feed_inventory',
        page: 'following_feed',
        properties: {
          inventory_size: inventorySize,
          tracked_page_count: trackedPageCount,
          inventory_page_sizes: inventoryPageSizes,
          inventory_entry_types: inventoryEntryTypes
        }
      });
    }
  }, [preFetchedEntries]);

  useEffect(() => {
    // restore feed position if navigation is through the browser's history traversal operation
    if (performance) {
      const navigationEntries = performance.getEntriesByType('navigation');
      if (navigationEntries.length) {
        if (navigationEntries[0].type === 'back_forward') {
          const scrollPosition = getScrollPosition();
          if (scrollPosition) {
            devLog(`[Feed] restoring scroll position to  ${scrollPosition}`);

            dispatch({
              type: UPDATE_IS_RESTORING_SCROLL_POSITION,
              payload: { status: true }
            });
            window.scrollTo(0, scrollPosition);
            clearScrollPosition();
            dispatch({
              type: UPDATE_IS_RESTORING_SCROLL_POSITION,
              payload: { status: false }
            });
          }
        }
      }
    } else {
      logMessage('feed scroll restoration failed. `performance` is undefined.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { hasMore } = pagination;
  const i18nPrefix = 'components.feed';

  // return the proper feed card that can be rendered
  const renderEntry = (params) => {
    const { key, index } = params;
    const entryProps = Object.assign(entries[index], {
      athleteProfileId, // for analytics - when viewing feed on athlete page
      clubId, // for analytics - when viewing feed on club details page
      index,
      page,
      feedType,
      key,
      locale,
      isAutoplayEnabled,
      isDeletable
    });

    let entry;
    const entityName = snakeCase(entryProps.entity);
    switch (entityName) {
      case FEED_ENTRY_TYPES.activity:
        entry = <Activity {...entryProps} />;
        break;
      case FEED_ENTRY_TYPES.suggestedActivity:
        entry = <SuggestedActivity {...entryProps} />;
        break;
      case FEED_ENTRY_TYPES.post:
        entry = <Post {...entryProps} />;
        break;
      case FEED_ENTRY_TYPES.fancyPromo:
        entry = <FancyPromo {...entryProps} />;
        break;
      case FEED_ENTRY_TYPES.performanceGoalCreated:
        entry = <PerformanceGoalCreated {...entryProps} />;
        break;
      case FEED_ENTRY_TYPES.challenge:
        entry = <ChallengeJoin {...entryProps} />;
        break;
      case FEED_ENTRY_TYPES.simplePromo:
        entry = <SimplePromo {...entryProps} />;
        break;
      case FEED_ENTRY_TYPES.groupActivity:
        entry = <GroupActivity {...entryProps} />;
        break;
      case FEED_ENTRY_TYPES.club:
        entry = <ClubJoin {...entryProps} />;
        break;
      default:
        logError(
          new Error(`Could not translate props into entry - ${entityName}`)
        );
        entry = <></>;
    }
    return (
      <div
        onClick={() => saveScrollPosition()}
        onKeyDown={() => saveScrollPosition()}
        role="button"
        tabIndex={0}
        className={styles.entryContainer}
        key={key}
      >
        {entry}
      </div>
    );
  };

  const endMessage = () => {
    if (page === 'dashboard' && !isLoading) {
      const content = `${i18nPrefix}.no_more.limit`;
      return (
        <div className={`${styles.feedPagination} ${styles.noEntries}`}>
          {I18n.t(content)}
          <br />
          <Markup
            noWrap={true}
            content={I18n.t(`${i18nPrefix}.no_more.full_html`, {
              athlete_id: currentAthleteId
            })}
          />
        </div>
      );
    }
    return null;
  };

  const loader = () => {
    return (
      <div className={styles.feedPagination}>
        <div className={styles.spinner}>
          {error ? I18n.t(`${i18nPrefix}.error_message`) : <Spinner />}
        </div>
      </div>
    );
  };

  const loadMoreRows = () => {
    let minBeforeEntry;
    let minRankEntry;
    if (entries.length > 0) {
      ({ minBeforeEntry, minRankEntry } = computeMinCursorData(entries));
    }
    const before = minBeforeEntry ? minBeforeEntry.cursorData.updated_at : null;
    const rank = minRankEntry ? minRankEntry.cursorData.rank : null;
    devLog(`[Feed#loadMoreRows] before: ${before} rank: ${rank}`);
    loadEntries({ cursorData: { before, rank } });
  };

  const handleNoClubPostsMessage = () => {
    return (
      <div className={styles.noClubPostsMessage}>
        {I18n.t('templates.discussions.discussion.no_posts_yet')}
        {canCreateClubPost && (
          <a
            className={styles.createPostLink}
            href={`/clubs/${clubId}/posts/new`}
          >
            {I18n.t('templates.discussions.discussion.create_first_club_post')}
          </a>
        )}
      </div>
    );
  };

  const [sentryRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: hasMore,
    onLoadMore: loadMoreRows,
    // When there is an error, we stop infinite loading.
    // It can be reactivated by setting "error" state as undefined.
    disabled: !!error,
    // `rootMargin` is passed to `IntersectionObserver`.
    // We can use it to trigger 'onLoadMore' when the sentry comes near to become
    // visible, instead of becoming fully visible on the screen.
    rootMargin: '0px 0px 1000px 0px'
  });

  return (
    <FeedContext.Provider value={providerState}>
      {entries.map((_, index) => renderEntry({ key: index, index }))}
      {!hasMore && endMessage()}
      {page === FEED_PAGES.club_posts &&
        !isLoading &&
        entries.length === 0 &&
        handleNoClubPostsMessage()}

      {(isLoading || hasMore) && <div ref={sentryRef}>{loader()}</div>}
    </FeedContext.Provider>
  );
};

Feed.propTypes = {
  currentAthleteId: PropTypes.number.isRequired,
  page: pageProps.isRequired,
  feedType: feedTypeProps.isRequired,
  athleteProfileId: PropTypes.string.isRequired,
  clubId: PropTypes.string.isRequired,
  useMockData: PropTypes.bool.isRequired,
  preFetchedEntries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locale: PropTypes.string.isRequired,
  isAutoplayEnabled: PropTypes.bool.isRequired,
  canCreateClubPost: PropTypes.bool,
  isDeletable: PropTypes.bool.isRequired
};

Feed.defaultProps = {
  canCreateClubPost: false
};

export default Feed;
